import { Component, Input, OnDestroy } from "@angular/core";
import { Capacitor } from "@capacitor/core";
import { ModalController } from "@ionic/angular";
import { FundingPdfModalComponent } from "../popups/funding-pdf-modal/funding-pdf-modal.component";

@Component({
  selector: "app-funding-visibility",
  templateUrl: "./funding-visibility.component.html",
  styleUrls: ["./funding-visibility.component.scss"],
})
export class FundingVisibilityComponent implements OnDestroy {
  @Input() groupApp: any;
  @Input() selectedLanguage: string;
  @Input() moBaseLanguage: string;
  @Input() placement = "homepage";

  private pdfModal: any;
  private openingPdfModal = false;

  private defaultLogoFrames = {
    el: "assets/funding/antagonistikotita-frame-el.jpg",
    en: "assets/funding/antagonistikotita-frame-en.jpg",
  };

  private defaultLabels = {
    aria: {
      el: "Πληροφορίες συγχρηματοδότησης ΕΣΠΑ",
      en: "NSRF co-funding information",
    },
    button: {
      el: "Άνοιγμα αφίσας συγχρηματοδότησης ΕΣΠΑ σε PDF",
      en: "Open the NSRF co-funding poster as a PDF",
    },
    close: {
      el: "Κλείσιμο",
      en: "Close",
    },
    screenReader: {
      el:
        "Το έργο συγχρηματοδοτείται από την Ευρωπαϊκή Ένωση, το ΕΣΠΑ 2021-2027 και το Πρόγραμμα Ανταγωνιστικότητα.",
      en:
        "The project is co-funded by the European Union, NSRF 2021-2027 and the Competitiveness Programme.",
    },
  };

  constructor(private modalController: ModalController) {}

  get isVisible() {
    // Web only: never show the banner on the native iOS/Android app.
    if (Capacitor.isNativePlatform()) {
      return false;
    }

    const settings = this.fundingSettings;

    if (!settings || !settings.active) {
      return false;
    }

    if (
      this.placement === "homepage" &&
      settings.homepage &&
      settings.homepage.active === false
    ) {
      return false;
    }

    return true;
  }

  get logoFrameSrc() {
    const settings = this.fundingSettings || {};
    const frames = settings.logoFrameImages || settings.logoFrames || {};
    const configuredFrame = this.getLocalizedValue(frames);

    if (configuredFrame) {
      return configuredFrame;
    }

    if (this.currentLanguage === "en") {
      return this.defaultLogoFrames.en;
    }

    return this.defaultLogoFrames.el;
  }

  get ariaLabel() {
    const settings = this.fundingSettings || {};
    return (
      this.getLocalizedValue(settings.ariaLabel) ||
      this.getLocalizedValue(this.defaultLabels.aria)
    );
  }

  get buttonLabel() {
    const settings = this.fundingSettings || {};
    return (
      this.getLocalizedValue(settings.buttonLabel) ||
      this.getLocalizedValue(this.defaultLabels.button)
    );
  }

  get screenReaderText() {
    const settings = this.fundingSettings || {};
    return (
      this.getLocalizedValue(settings.screenReaderText || settings.statement) ||
      this.getLocalizedValue(this.defaultLabels.screenReader)
    );
  }

  // The poster is published per group and per language, e.g.
  // fundingVisibility.pdf = { el: "https://…/espa-el.pdf", en: "https://…/espa-en.pdf" }
  // The value reaches a trusted resource URL, and it comes from the remote groupApp payload,
  // so anything that is not an absolute https link is refused and the banner stays static.
  get pdfUrl() {
    const settings = this.fundingSettings || {};
    const url = this.getLocalizedValue(settings.pdf);

    return /^https:\/\//i.test(url) ? url : "";
  }

  async openPdf() {
    // create() is async, so the modal must be claimed synchronously or a double
    // activation stacks a second modal and orphans the first.
    if (!this.pdfUrl || this.pdfModal || this.openingPdfModal) {
      return;
    }

    this.openingPdfModal = true;

    try {
      const modal = await this.modalController.create({
        component: FundingPdfModalComponent,
        componentProps: {
          pdfUrl: this.pdfUrl,
          modalTitle: this.ariaLabel,
          closeLabel: this.getLocalizedValue(this.defaultLabels.close),
        },
        cssClass: "fundingPdfModal",
        htmlAttributes: { "aria-label": this.ariaLabel },
      });

      this.pdfModal = modal;
      await modal.present();

      modal.onDidDismiss().then(() => {
        this.pdfModal = null;
      });
    } finally {
      this.openingPdfModal = false;
    }
  }

  ngOnDestroy() {
    // The modal lives on the ion-app overlay layer, so a route change would strand it.
    if (this.pdfModal) {
      this.pdfModal.dismiss();
      this.pdfModal = null;
    }
  }

  private get fundingSettings() {
    if (!this.groupApp) {
      return null;
    }

    return this.groupApp.fundingVisibility || this.groupApp.funding_visibility;
  }

  private get currentLanguage() {
    return this.selectedLanguage || this.moBaseLanguage || "el";
  }

  private getLocalizedValue(value: any) {
    if (!value) {
      return "";
    }

    if (typeof value === "string") {
      return value;
    }

    if (value[this.currentLanguage]) {
      return value[this.currentLanguage];
    }

    if (this.moBaseLanguage && value[this.moBaseLanguage]) {
      return value[this.moBaseLanguage];
    }

    if (value.el) {
      return value.el;
    }

    if (value.en) {
      return value.en;
    }

    const keys = Object.keys(value);
    const firstKey = keys.find((key) => !!value[key]);

    return firstKey ? value[firstKey] : "";
  }
}
